import Chat from '@common/Chat'
import SupportChatButton from '@common/Chat/SupportChatButton/SupportChatButton'
import { useIsChatEnabled } from '@common/Chat/hooks/useIsChatEnabled'
import useIsTechSupport from '@common/Chat/SupportChatButton/useIsTechSupport'
import PlatformSupportLauncher from '@common/PlatformSupport/PlatformSupportLauncher'
import { LessonChatsList } from '@common/LessonChats'
import { isLessonChatsRoute, useLessonChats } from '@/helpers/lessonChats'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useIntegrationChatDock } from './integrationChatDockHooks'
import IntegrationChatDockLauncher from './IntegrationChatDockLauncher'

type IntegrationChatDockFloatingActionsProps = {
  chatState: {
    changePage: (...args: unknown[]) => void
    page?: string
    params?: {
      selectedTab?: string
      lessonId?: string | number
    }
    [key: string]: unknown
  }
}

const getFullscreenElement = () => {
  if (typeof document === 'undefined') return null

  return document.fullscreenElement ||
    (document as Document & { webkitFullscreenElement?: Element | null })
      .webkitFullscreenElement
}

const isLessonVideoFallbackFullscreen = () => (
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('lesson-video-fallback-fullscreen')
)

const getFullscreenHost = () => {
  const fullscreenElement = getFullscreenElement()

  if (fullscreenElement instanceof HTMLElement && fullscreenElement.tagName !== 'IFRAME') {
    return fullscreenElement
  }

  if (!isLessonVideoFallbackFullscreen()) return null

  return document.querySelector<HTMLElement>(
    '[data-lesson-video-fullscreen="true"]',
  )
}

const IntegrationChatDockFloatingActions = ({
  chatState,
}: IntegrationChatDockFloatingActionsProps) => {
  const { expanded } = useIntegrationChatDock()
  const { chatIsDisabledButTechnicalSupportIsEnabled } = useIsTechSupport()
  const { isChatEnabled, isChatFloatingIconEnabled } = useIsChatEnabled()
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(false)
  const [fullscreenHost, setFullscreenHost] = useState<HTMLElement | null>(null)
  const isFullscreen = Boolean(fullscreenHost)
  const location = useLocation()
  const previewLessonId = useSelector((state: any) => state?.lessonPreview?.id)
  const queryLessonId = new URLSearchParams(location.search).get('lesson')
  const isLessonChatContext = chatState.params?.selectedTab === 'lesson'
  const lessonId = queryLessonId || (
    location.pathname === '/preview-lesson' ? previewLessonId : null
  ) || (isLessonChatContext ? chatState.params?.lessonId : null)
  const isLessonPage = isLessonChatsRoute({
    pathname: location.pathname,
    lessonId,
  })
  const { chats: lessonChats } = useLessonChats(
    (isFullscreen || isLessonPage) ? lessonId : null,
  )
  const isLessonChatOpen =
    chatState.page === 'inChat' && isLessonChatContext
  const isIntegrationDockExpanded = expanded && !isLessonPage
  const isFullscreenDockVisible = !isIntegrationDockExpanded && isFullscreen && (
    isLessonChatOpen || lessonChats.length > 0
  )

  useEffect(() => {
    const syncFullscreenHost = () => {
      const host = getFullscreenHost()

      setFullscreenHost(host)
      if (host) setIsMobileCollapsed(false)
    }

    document.addEventListener('fullscreenchange', syncFullscreenHost)
    document.addEventListener('webkitfullscreenchange', syncFullscreenHost)
    window.addEventListener('lesson-video-fullscreenchange', syncFullscreenHost)
    syncFullscreenHost()

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenHost)
      document.removeEventListener('webkitfullscreenchange', syncFullscreenHost)
      window.removeEventListener('lesson-video-fullscreenchange', syncFullscreenHost)
    }
  }, [])

  useEffect(() => {
    const root = document.documentElement

    root.classList.toggle(
      'lesson-chat-fullscreen-dock-visible',
      isFullscreenDockVisible,
    )
    root.classList.toggle(
      'lesson-chat-fullscreen-dock-open',
      isFullscreenDockVisible && isLessonChatOpen,
    )

    return () => {
      root.classList.remove('lesson-chat-fullscreen-dock-visible')
      root.classList.remove('lesson-chat-fullscreen-dock-open')
    }
  }, [isFullscreenDockVisible, isLessonChatOpen])

  if (isIntegrationDockExpanded) {
    return null
  }

  if (isLessonPage && !isLessonChatOpen && lessonChats.length === 0) {
    return null
  }

  const actions = (
    <div
      className={classNames('chatActions', {
        chatActionsMobileCollapsed: isMobileCollapsed,
        chatActionsFullscreen: isFullscreen,
        chatActionsFullscreenVisible: isFullscreenDockVisible,
        chatActionsLessonChatOpen: isFullscreen && isLessonChatOpen,
      })}
    >
      {!isFullscreen && !isLessonPage && (
        <>
          <button
            type="button"
            className="chatActionsMobileToggle"
            aria-label={isMobileCollapsed ? 'Показать чаты' : 'Скрыть чаты'}
            aria-expanded={!isMobileCollapsed}
            onClick={() => setIsMobileCollapsed((collapsed) => !collapsed)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
          <IntegrationChatDockLauncher className="chatAction" />
          <SupportChatButton className="chatAction" />
        </>
      )}
      {(isFullscreen || isLessonPage) && !isLessonChatOpen && lessonChats.length > 0 && (
        <LessonChatsList
          chats={lessonChats}
          compact
          className="lessonChatQuickActions"
          onSelect={(chatId: string | number) => chatState.changePage(
            'inChat',
            chatId,
            'create',
            { selectedTab: 'lesson', lessonId },
          )}
        />
      )}
      {(
        isLessonChatOpen ||
        (!isFullscreen && !isLessonPage && (
          isChatEnabled || chatIsDisabledButTechnicalSupportIsEnabled
        ))
      ) && (
        <Chat
          chatState={chatState}
          chatResize={!isFullscreen}
          className="chatAction"
          hideLauncher={isFullscreen || !isChatFloatingIconEnabled}
        />
      )}
      {!isFullscreen && !isLessonPage && <PlatformSupportLauncher className="chatAction" />}
    </div>
  )

  return fullscreenHost ? createPortal(actions, fullscreenHost) : actions
}

export default IntegrationChatDockFloatingActions
